import { Component, Input, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MesCusto, CustoUsuarioItem } from '../../shared/model/relatorio-custo.models';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthService } from '../../shared/service/auth.service';
import { environment } from '../../../../environments/environment';
import { BalaoAvisoComponent } from '../../shared/components/balao-aviso/balao-aviso.component';

interface LinhaKV { nome: string; total: number }
@Component({
  selector: 'app-visao-ranking',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, BalaoAvisoComponent],
  templateUrl: './visao-ranking.component.html',
  styleUrls: ['./visao-ranking.component.css'],
})
export class VisaoRankingComponent {

  private readonly domSanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);

  private anoSig = signal<number>(new Date().getFullYear());
  @Input({ required: true })
  set ano(v: number) { this.anoSig.set(v); }
  get ano() { return this.anoSig(); }

  private mesSig = signal<MesCusto | null>(null);
  @Input()
  set mes(v: MesCusto | null) { this.mesSig.set(v); }
  get mes() { return this.mesSig(); }

  private mesSelecionadoSig = signal<number | null>(null);
  @Input()
  set mesSelecionado(v: number | null) { this.mesSelecionadoSig.set(v); }
  get mesSelecionado() { return this.mesSelecionadoSig(); }

  private mesesSig = signal<MesCusto[]>([]);
  @Input()
  set meses(v: MesCusto[] | null | undefined) { this.mesesSig.set(v ?? []); }
  get meses() { return this.mesesSig(); }

  @Input() loading = false;

  private sum(a: number, b: number) { return (a ?? 0) + (b ?? 0); }

  gerarUrlUsuarioImagem(uuid: string, extensao: string): SafeUrl {
  const grupo = this.authService.getUsuarioLogado()?.grupo;
  return this.domSanitizer.bypassSecurityTrustUrl(
    `${environment.urlRepositorioClaribot}/${grupo}/usuario-img/${uuid}.${extensao}`
  );
}

  private mergeUsuarios(map: Map<number, CustoUsuarioItem>, u: CustoUsuarioItem) {
    const curr = map.get(u.usuarioId);
    if (!curr) {
      map.set(u.usuarioId, {
        ...u,
        total: u.total ?? 0,
        disparos: u.disparos ?? 0,
        disparosIndividuais: u.disparosIndividuais ?? 0,
        templates: u.templates ?? 0,
      });
      return;
    }

    map.set(u.usuarioId, {
      ...curr,
      total: this.sum(curr.total ?? 0, u.total ?? 0),
      disparos: this.sum(curr.disparos ?? 0, u.disparos ?? 0),
      disparosIndividuais: this.sum(curr.disparosIndividuais ?? 0, u.disparosIndividuais ?? 0),
      templates: this.sum(curr.templates ?? 0, u.templates ?? 0),
    });
  }

  private monthNamePT(mes: number) {
    const nomes = [
      'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
      'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
    ];
    return nomes[(mes ?? 1) - 1] ?? `Mês ${mes}`;
  }

  private getRankingBase(): CustoUsuarioItem[] {
    const ms = this.mesSelecionadoSig();
    const m = this.mesSig();
    const meses = this.mesesSig();

    if (ms != null) {
      return m?.rankingUsuarios ?? [];
    }

    const all = (meses ?? []).flatMap(x => x?.rankingUsuarios ?? []);
    const map = new Map<number, CustoUsuarioItem>();
    for (const u of all) this.mergeUsuarios(map, u);
    return [...map.values()];
  }

  titulo = computed(() => {
    const ms = this.mesSelecionadoSig();
    const m = this.mesSig();

    if (ms != null) {
      const nome = m?.nomeCompleto ?? this.monthNamePT(ms);
      return `Rankings de Custos - ${nome}`;
    }

    return 'Ranking de Custos';
  });

  rankingUsuarios = computed(() => {
    const base = this.getRankingBase();
    return [...base].sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
  });

  setores = computed<LinhaKV[]>(() => {
    const porSetor = new Map<string, number>();
    for (const u of this.getRankingBase()) {
      const key = u.setorNome ?? 'Sem setor';
      porSetor.set(key, (porSetor.get(key) ?? 0) + (u.total ?? 0));
    }
    return [...porSetor.entries()]
      .map(([nome, total]) => ({ nome, total }))
      .sort((a, b) => b.total - a.total);
  });

  disparos = computed<LinhaKV[]>(() => {
    const ms = this.mesSelecionadoSig();
    const m = this.mesSig();
    const meses = this.mesesSig();

    if (ms != null) {
      const list = m?.porDisparo ?? [];
      return [...list]
        .map(x => ({ nome: x.nome, total: x.total }))
        .sort((a, b) => b.total - a.total);
    }

    const acc = new Map<string, number>();
    for (const mes of (meses ?? [])) {
      for (const item of (mes?.porDisparo ?? [])) {
        const key = item.nome ?? 'Disparo';
        acc.set(key, (acc.get(key) ?? 0) + (item.total ?? 0));
      }
    }
    return [...acc.entries()]
      .map(([nome, total]) => ({ nome, total }))
      .sort((a, b) => b.total - a.total);
  });

  disparosIndividuais = computed<LinhaKV[]>(() => {
    const ms = this.mesSelecionadoSig();
    const m = this.mesSig();
    const meses = this.mesesSig();

    if (ms != null) {
      const list = m?.porDisparoIndividual ?? [];
      return [...list]
        .map(x => ({ nome: x.nome, total: x.total }))
        .sort((a, b) => b.total - a.total);
    }

    const acc = new Map<string, number>();
    for (const mes of (meses ?? [])) {
      for (const item of (mes?.porDisparoIndividual ?? [])) {
        const key = item.nome ?? 'Disparo';
        acc.set(key, (acc.get(key) ?? 0) + (item.total ?? 0));
      }
    }
    return [...acc.entries()]
      .map(([nome, total]) => ({ nome, total }))
      .sort((a, b) => b.total - a.total);
  });

  templates = computed<LinhaKV[]>(() => {
    const ms = this.mesSelecionadoSig();
    const m = this.mesSig();
    const meses = this.mesesSig();

    if (ms != null) {
      const list = m?.porTemplateRanking ?? [];
      return [...list]
        .map(x => ({ nome: x.nome, total: x.total }))
        .sort((a, b) => b.total - a.total);
    }

    const acc = new Map<string, number>();
    for (const mes of (meses ?? [])) {
      for (const item of (mes?.porTemplateRanking ?? [])) {
        const key = item.nome ?? 'Template';
        acc.set(key, (acc.get(key) ?? 0) + (item.total ?? 0));
      }
    }
    return [...acc.entries()]
      .map(([nome, total]) => ({ nome, total }))
      .sort((a, b) => b.total - a.total);
  });
  semDados = computed(() =>
    !this.loading &&
    this.rankingUsuarios().length === 0 &&
    this.setores().length === 0 &&
    this.disparos().length === 0 &&
    this.disparosIndividuais().length === 0 &&
    this.templates().length === 0
  );

 fotoUsuarioOrFallback(u: any): SafeUrl | string {
  const uuid = u?.uuid;
  const ext = u?.extensaoImg;

  if (uuid && ext) {
    return this.gerarUrlUsuarioImagem(uuid, ext);
  }

  return 'assets/sem-img.svg';
}

  formatUSD(value: number) {
    return (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  trackUser(_: number, u: CustoUsuarioItem) {
    return u.usuarioId;
  }

  trackKV(_: number, x: LinhaKV) {
    return x.nome;
  }

  nomeCurtoUsuario(nome: string | null | undefined) {
    const full = (nome ?? '').trim().replace(/\s+/g, ' ');
    if (!full) return '';

    const parts = full.split(' ');
    return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : parts[0];
  }

  ellipsisDepois3PartesUnderline(texto: string | null | undefined) {
    const full = (texto ?? '').trim();
    if (!full) return '';

    const parts = full.split('_').filter(p => p.length > 0);
    if (parts.length <= 2) return full;

    return `${parts.slice(0, 2).join('_')}...`;
  }
}
